import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Upload,
  Button,
  Image,
  Select,
  DatePicker,
  notification,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Joi from "joi";
import axios from "axios";
import config from "../../../../../config/config";

const { Option } = Select;

const RecipeVideoCreate = ({ isVisible, handleClose, base_url }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [categories, setCategories] = useState([]);

  const joiOptions = config.joiOptions;

  /* ===================== FETCH CATEGORIES ===================== */
  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${base_url}/recipe/all`);
      setCategories(res.data.categories || []);
    } catch {
      notification.error({ message: "Failed to load categories" });
    }
  };

  useEffect(() => {
    if (isVisible) fetchCategories();
  }, [isVisible]);

  /* ===================== VALIDATION ===================== */
  const validate = (values) => {
    const schema = Joi.object({
      title: Joi.string().min(1).required().label("Recipe Title"),
      date2: Joi.date().required().label("Video Date"),
      videoUrl: Joi.string().min(5).required().label("Video URL"),
      categoryId: Joi.string().allow(null, ""),
    });

    const { error } = schema.validate(values, joiOptions);
    const errors = {};

    if (error) {
      error.details.forEach((item) => {
        errors[item.path[0]] = item.message;
      });
    }

    if (!imageFile) {
      errors.desktopImageUrl = "Recipe Video Image is required";
    }

    return errors;
  };

  /* ===================== SUBMIT ===================== */
  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      date2: values.date2?.toISOString(),
    };

    const errors = validate(payload);
    setFormError(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("date2", payload.date2);
      formData.append("videoUrl", values.videoUrl);
      formData.append("desktopImageUrl", imageFile);

      if (values.categoryId) {
        formData.append("categoryId", values.categoryId);
      }

      await axios.post(`${base_url}/recipe/video/add`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      notification.success({
        message: "Recipe video created successfully",
      });

      form.resetFields();
      setImageFile(null);
      setImagePreview(null);
      setFormError({});
      handleClose();
    } catch (err) {
      notification.error({
        message: "Create failed",
        description: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ===================== IMAGE HANDLER ===================== */
  const handleImageChange = (file) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    return false;
  };

  return (
    <Modal
      title="Add Recipe Video"
      visible={isVisible}
      onCancel={handleClose}
      okText="Create"
      cancelText="Cancel"
      confirmLoading={loading}
      onOk={() => form.validateFields().then((values) => handleSubmit(values))}
      width={700}
      destroyOnClose
    >
      <Form layout="vertical" form={form}>
        {/* TITLE */}
        <Form.Item
          label="Recipe Title"
          name="title"
          required
          {...(formError.title && {
            help: formError.title,
            validateStatus: "error",
          })}
        >
          <Input placeholder="Enter Recipe title" />
        </Form.Item>

        {/* DATE */}
        <Form.Item
          label="Video Date"
          name="date2"
          required
          {...(formError.date2 && {
            help: formError.date2,
            validateStatus: "error",
          })}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>

        {/* VIDEO URL */}
        <Form.Item
          label="Video URL"
          name="videoUrl"
          required
          {...(formError.videoUrl && {
            help: formError.videoUrl,
            validateStatus: "error",
          })}
        >
          <Input
            placeholder="https://youtu.be/lPrNTCMkw1A"
          />
        </Form.Item>

        {/* CATEGORY */}
        <Form.Item label="Category (Optional)" name="categoryId">
          <Select allowClear placeholder="Select category">
            {categories.map((cat) => (
              <Option key={cat._id} value={cat._id}>
                {cat.categoryTitle}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* IMAGE */}
        <Form.Item
          label="Recipe Video Image"
          required
          extra="Recommended size: 420 x 500 px"
          {...(formError.desktopImageUrl && {
            help: formError.desktopImageUrl,
            validateStatus: "error",
          })}
        >
          <Upload
            beforeUpload={handleImageChange}
            maxCount={1}
            accept="image/*"
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>Select Image</Button>
          </Upload>

          {imagePreview && (
            <div style={{ marginTop: 10 }}>
              <Image
                src={imagePreview}
                width={160}
                height={90}
                style={{ objectFit: "cover", borderRadius: 6 }}
              />
            </div>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RecipeVideoCreate;
